import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'

import { useConcessions } from '../../../hooks/useConcessions'
import { usePremiums } from '../../../hooks/usePremiums'
import {
    useNewLeaseOfferPreview,
    useOfferUnitConfigs,
    useRenewalOffers,
} from '../../../hooks/useRenewalOffers'
import { NewLeaseOfferPreview } from '../../../models/Concession'
import {
    Concession,
    OfferUnitConfig,
    Premium,
} from '../../../models/Concession'
import { PipelineRecord } from './types'
import { ConcessionModal } from '../../Concessions/ConcessionModal'
import { PremiumModal } from '../../Concessions/PremiumModal'

interface SendLeaseOfferDialogProps {
    open: boolean
    record: PipelineRecord | null
    leasingSeasonId?: number
    onClose: () => void
    onSent?: () => void
}

function unitTypeLabel(config: OfferUnitConfig): string {
    if (config.bed_count != null && config.bath_count != null) {
        return `${config.name} (${config.bed_count}×${config.bath_count})`
    }
    return config.name
}

function formatMoney(value: number): string {
    const abs = Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
    return value < 0 ? `-$${abs}` : `$${abs}`
}

function toAmount(value?: string | null): number {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

function yesNo(value: boolean | null | undefined): string {
    if (value === true) return 'Yes'
    if (value === false) return 'No'
    return ''
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
    return (
        <TextField
            label={label}
            value={value}
            size="small"
            variant="outlined"
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            disabled
        />
    )
}

const twoColumn = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
} as const

const CREATE_OPTION = '__new__'

function AddableOptions({
    title,
    addLabel,
    createLabel,
    options,
    selectedIds,
    onAdd,
    onRemove,
    onCreate,
}: {
    title: string
    addLabel: string
    createLabel: string
    options: Array<{ id: number; label: string }>
    selectedIds: number[]
    onAdd: (id: number) => void
    onRemove: (id: number) => void
    onCreate: () => void
}) {
    const selected = options.filter((option) => selectedIds.includes(option.id))
    const available = options.filter(
        (option) => !selectedIds.includes(option.id),
    )
    return (
        <FormControl component="fieldset" fullWidth margin="dense" size="small">
            <Typography variant="subtitle2">{title}</Typography>
            {selected.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    None
                </Typography>
            ) : (
                <FormGroup>
                    {selected.map((option) => (
                        <FormControlLabel
                            key={option.id}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked
                                    onChange={() => onRemove(option.id)}
                                />
                            }
                            label={option.label}
                        />
                    ))}
                </FormGroup>
            )}
            <FormControl
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
            >
                <InputLabel>{addLabel}</InputLabel>
                <Select
                    label={addLabel}
                    value=""
                    onChange={(event) => {
                        const value = String(event.target.value)
                        if (value === CREATE_OPTION) {
                            onCreate()
                            return
                        }
                        if (value) onAdd(Number(value))
                    }}
                >
                    {available.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.label}
                        </MenuItem>
                    ))}
                    <MenuItem value={CREATE_OPTION}>{createLabel}</MenuItem>
                </Select>
            </FormControl>
        </FormControl>
    )
}

function SheetSection({
    title,
    children,
}: {
    title: string
    children: React.ReactNode
}) {
    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                {title}
            </Typography>
            <Box
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    columnGap: 12,
                }}
            >
                {children}
            </Box>
        </Box>
    )
}

function displayMoney(value?: string | null): string {
    return formatMoney(toAmount(value))
}

function withOccupantCharges(
    lines: Array<{ kind: string; label: string; amount: number }>,
    preview: NewLeaseOfferPreview | undefined,
) {
    const next = [...lines]
    if (!next.some((line) => line.kind === 'base_rent')) {
        next.unshift({
            kind: 'base_rent',
            label: 'Base rent',
            amount: 0,
        })
    }
    if (!next.some((line) => line.kind === 'pet_rent')) {
        next.push({
            kind: 'pet_rent',
            label: 'Monthly pet rent',
            amount: toAmount(preview?.pet_rent),
        })
    }
    if (!next.some((line) => line.kind === 'parking_fee')) {
        next.push({
            kind: 'parking_fee',
            label: 'Monthly parking fee',
            amount: toAmount(preview?.parking_fee),
        })
    }
    return next
}

export const SendLeaseOfferDialog: React.FC<SendLeaseOfferDialogProps> = ({
    open,
    record,
    leasingSeasonId,
    onClose,
    onSent,
}) => {
    const { unitConfigs, isLoading } = useOfferUnitConfigs(
        open,
        leasingSeasonId,
    )
    const { concessions } = useConcessions()
    const { premiums } = usePremiums()
    const { createNewLeaseOffer } = useRenewalOffers()
    const [unitConfigId, setUnitConfigId] = useState<number | ''>('')
    const [premiumIds, setPremiumIds] = useState<number[]>([])
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [concessionIds, setConcessionIds] = useState<number[]>([])
    const [expiresAt, setExpiresAt] = useState('')
    const [createdPremiums, setCreatedPremiums] = useState<Premium[]>([])
    const [createdConcessions, setCreatedConcessions] = useState<Concession[]>(
        [],
    )
    const [premiumModalOpen, setPremiumModalOpen] = useState(false)
    const [concessionModalOpen, setConcessionModalOpen] = useState(false)

    const previewBody = useMemo(() => {
        if (!open || record?.leadId == null) return null
        return {
            lead: record.leadId,
            unit_config: unitConfigId === '' ? null : Number(unitConfigId),
            proposed_start_date: startDate || null,
            proposed_end_date: endDate || null,
            leasing_season: leasingSeasonId ?? null,
            premiums: premiumIds,
            concessions: concessionIds,
        }
    }, [
        open,
        record?.leadId,
        unitConfigId,
        startDate,
        endDate,
        leasingSeasonId,
        premiumIds,
        concessionIds,
    ])
    const { data: preview } = useNewLeaseOfferPreview(open, previewBody)

    const selectedConfig =
        unitConfigId === ''
            ? undefined
            : unitConfigs.find((row) => row.id === unitConfigId)
    const selectablePremiums = useMemo(() => {
        const selected = new Set(premiumIds)
        const known = new Set(premiums.map((premium) => premium.id))
        return premiums
            .concat(createdPremiums.filter((premium) => !known.has(premium.id)))
            .filter((premium) => premium.is_active || selected.has(premium.id))
    }, [premiums, premiumIds, createdPremiums])
    const selectableConcessions = useMemo(() => {
        const selected = new Set(concessionIds)
        const known = new Set(concessions.map((concession) => concession.id))
        return concessions
            .concat(
                createdConcessions.filter(
                    (concession) => !known.has(concession.id),
                ),
            )
            .filter(
                (concession) =>
                    concession.is_active || selected.has(concession.id),
            )
    }, [concessions, concessionIds, createdConcessions])

    const chargeLines = useMemo(() => {
        if (
            preview &&
            !preview.missing_base_rent &&
            preview.charge_lines.length > 0
        ) {
            return withOccupantCharges(
                preview.charge_lines.map((line) => ({
                    kind: line.kind,
                    label: line.label,
                    amount: toAmount(line.amount),
                })),
                preview,
            )
        }
        const lines: Array<{ kind: string; label: string; amount: number }> = []
        const baseRent = toAmount(selectedConfig?.default_rent)
        if (selectedConfig) {
            lines.push({
                kind: 'base_rent',
                label: `Base rent (${selectedConfig.name})`,
                amount: baseRent,
            })
        }
        selectablePremiums
            .filter((premium) => premiumIds.includes(premium.id))
            .forEach((premium: Premium) => {
                lines.push({
                    kind: 'premium',
                    label: premium.name,
                    amount: toAmount(premium.amount),
                })
            })
        selectableConcessions
            .filter((concession) => concessionIds.includes(concession.id))
            .forEach((concession: Concession) => {
                lines.push({
                    kind: 'concession',
                    label: concession.name,
                    amount: -toAmount(concession.credit_amount),
                })
            })
        return withOccupantCharges(lines, preview)
    }, [
        preview,
        selectedConfig,
        selectablePremiums,
        premiumIds,
        selectableConcessions,
        concessionIds,
    ])

    const monthlyTotal = chargeLines.reduce((sum, line) => sum + line.amount, 0)
    const hasBaseRent = toAmount(selectedConfig?.default_rent) > 0

    const syncExpiryWithMoveIn = (nextStart: string, previousStart: string) => {
        setExpiresAt((current) =>
            current === '' || current === previousStart ? nextStart : current,
        )
    }

    useEffect(() => {
        if (!open || !record) return
        setUnitConfigId(record.preferred_unit_config_id ?? '')
        setPremiumIds(record.preferred_premium_ids ?? [])
        setConcessionIds([])
        setCreatedPremiums([])
        setCreatedConcessions([])
        setPremiumModalOpen(false)
        setConcessionModalOpen(false)
        setExpiresAt('')
        setStartDate('')
        setEndDate('')
    }, [open, record])

    useEffect(() => {
        if (!open) return
        const datesFrom =
            selectedConfig ??
            (unitConfigs.length > 0 ? unitConfigs[0] : undefined)
        const fallbackStart = datesFrom?.lease_start_date || ''
        const fallbackEnd = datesFrom?.lease_end_date || ''
        if (fallbackStart) {
            setStartDate((current) => current || fallbackStart)
            setExpiresAt((current) => current || fallbackStart)
        }
        if (fallbackEnd) {
            setEndDate((current) => current || fallbackEnd)
        }
    }, [open, unitConfigId, unitConfigs])

    const addId = (
        id: number,
        setIds: React.Dispatch<React.SetStateAction<number[]>>,
    ) => {
        setIds((current) => (current.includes(id) ? current : [...current, id]))
    }

    const removeId = (
        id: number,
        setIds: React.Dispatch<React.SetStateAction<number[]>>,
    ) => {
        setIds((current) => current.filter((row) => row !== id))
    }

    const handleSend = async () => {
        if (
            record?.leadId == null ||
            unitConfigId === '' ||
            !hasBaseRent ||
            monthlyTotal <= 0 ||
            !startDate ||
            !endDate
        ) {
            return
        }
        try {
            await createNewLeaseOffer.mutateAsync({
                lead: record.leadId,
                unit_config: Number(unitConfigId),
                proposed_start_date: startDate,
                proposed_end_date: endDate,
                leasing_season: leasingSeasonId,
                premiums: premiumIds,
                concessions: concessionIds,
                expires_at: expiresAt ? `${expiresAt}T23:59:59` : null,
                send: true,
            })
            onSent?.()
            onClose()
        } catch {
            // Toast is handled by the mutation.
        }
    }

    const title = record?.name
        ? `Send Lease Offer to ${record.name}`
        : 'Send Lease Offer'

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Box maxWidth={280}>
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            margin="dense"
                            disabled={isLoading}
                        >
                            <InputLabel>Floor plan</InputLabel>
                            <Select
                                label="Floor plan"
                                value={unitConfigId}
                                onChange={(event) =>
                                    setUnitConfigId(
                                        event.target.value === ''
                                            ? ''
                                            : Number(event.target.value),
                                    )
                                }
                            >
                                {unitConfigs.map((config) => (
                                    <MenuItem key={config.id} value={config.id}>
                                        {unitTypeLabel(config)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box style={twoColumn}>
                        <AddableOptions
                            title="Premiums"
                            addLabel="Add premium"
                            createLabel="New premium…"
                            selectedIds={premiumIds}
                            onAdd={(id) => addId(id, setPremiumIds)}
                            onRemove={(id) => removeId(id, setPremiumIds)}
                            onCreate={() => setPremiumModalOpen(true)}
                            options={selectablePremiums.map((premium) => ({
                                id: premium.id,
                                label: `${premium.name} (${formatMoney(
                                    toAmount(premium.amount),
                                )}/mo)`,
                            }))}
                        />
                        <AddableOptions
                            title="Concessions"
                            addLabel="Add concession"
                            createLabel="New concession…"
                            selectedIds={concessionIds}
                            onAdd={(id) => addId(id, setConcessionIds)}
                            onRemove={(id) => removeId(id, setConcessionIds)}
                            onCreate={() => setConcessionModalOpen(true)}
                            options={selectableConcessions.map(
                                (concession) => ({
                                    id: concession.id,
                                    label: `${concession.name} (${formatMoney(
                                        -toAmount(concession.credit_amount),
                                    )}/mo)`,
                                }),
                            )}
                        />
                    </Box>
                    <Box
                        mt={1}
                        style={{
                            ...twoColumn,
                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        }}
                    >
                        <TextField
                            label="Move-in date"
                            type="date"
                            size="small"
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(event) => {
                                const next = event.target.value
                                syncExpiryWithMoveIn(next, startDate)
                                setStartDate(next)
                            }}
                        />
                        <TextField
                            label="Move-out date"
                            type="date"
                            size="small"
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(event) => setEndDate(event.target.value)}
                        />
                        <ReadOnlyField
                            label="Term (months)"
                            value={
                                preview?.proposed_term_months != null
                                    ? String(preview.proposed_term_months)
                                    : ''
                            }
                        />
                        <TextField
                            label="Offer expires"
                            type="date"
                            size="small"
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            value={expiresAt}
                            onChange={(event) =>
                                setExpiresAt(event.target.value)
                            }
                        />
                    </Box>
                    <Box mt={2} style={twoColumn}>
                        <SheetSection title="Resident">
                            <ReadOnlyField
                                label="First name"
                                value={preview?.resident_first_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Middle name"
                                value={preview?.resident_middle_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Last name"
                                value={preview?.resident_last_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Email"
                                value={preview?.resident_email ?? ''}
                            />
                            <ReadOnlyField
                                label="Phone"
                                value={preview?.resident_phone ?? ''}
                            />
                        </SheetSection>
                        <SheetSection title="Guarantor">
                            <ReadOnlyField
                                label="First name"
                                value={preview?.guarantor_first_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Middle name"
                                value={preview?.guarantor_middle_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Last name"
                                value={preview?.guarantor_last_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Email"
                                value={preview?.guarantor_email ?? ''}
                            />
                            <ReadOnlyField
                                label="Phone"
                                value={preview?.guarantor_phone ?? ''}
                            />
                            <ReadOnlyField
                                label="Relationship"
                                value={preview?.guarantor_relationship ?? ''}
                            />
                        </SheetSection>
                    </Box>
                    <Box mt={2} style={twoColumn}>
                        <SheetSection title="Pet">
                            <ReadOnlyField
                                label="Bringing a pet"
                                value={yesNo(preview?.pet_declared)}
                            />
                            <ReadOnlyField
                                label="Name"
                                value={preview?.pet_name ?? ''}
                            />
                            <ReadOnlyField
                                label="Animal type"
                                value={preview?.pet_animal_type ?? ''}
                            />
                            <ReadOnlyField
                                label="Breed"
                                value={preview?.pet_breed ?? ''}
                            />
                            <ReadOnlyField
                                label="Weight"
                                value={preview?.pet_weight ?? ''}
                            />
                        </SheetSection>
                        <SheetSection title="Parking">
                            <ReadOnlyField
                                label="Needs parking"
                                value={yesNo(preview?.parking_declared)}
                            />
                            <ReadOnlyField
                                label="Vehicle make"
                                value={preview?.vehicle_make ?? ''}
                            />
                            <ReadOnlyField
                                label="Vehicle model"
                                value={preview?.vehicle_model ?? ''}
                            />
                            <ReadOnlyField
                                label="Vehicle color"
                                value={preview?.vehicle_color ?? ''}
                            />
                            <ReadOnlyField
                                label="License plate"
                                value={preview?.vehicle_license_plate ?? ''}
                            />
                            <ReadOnlyField
                                label="Plate state"
                                value={
                                    preview?.vehicle_license_plate_state ?? ''
                                }
                            />
                        </SheetSection>
                    </Box>
                    <Box mt={2} style={twoColumn}>
                        <Box
                            mt={1}
                            mb={1}
                            p={1.5}
                            style={{
                                border: '1px solid #e6e9ed',
                                borderRadius: 8,
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Charges
                            </Typography>
                            {chargeLines.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Select a floor plan to see charges.
                                </Typography>
                            ) : (
                                chargeLines.map((line) => (
                                    <Box
                                        key={`${line.kind}-${line.label}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="body2">
                                            {line.label}
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatMoney(line.amount)}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mt={1}
                                pt={1}
                                style={{ borderTop: '1px solid #e6e9ed' }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 700 }}
                                >
                                    Monthly total
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 700 }}
                                >
                                    {formatMoney(monthlyTotal)}
                                </Typography>
                            </Box>
                            {selectedConfig && !hasBaseRent && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    style={{ display: 'block', marginTop: 8 }}
                                >
                                    This floor plan has no new-tenant rent in
                                    property data.
                                </Typography>
                            )}
                        </Box>
                        <Box
                            mt={1}
                            mb={1}
                            p={1.5}
                            style={{
                                border: '1px solid #e6e9ed',
                                borderRadius: 8,
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Due at signing
                            </Typography>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">
                                    Pet deposit
                                </Typography>
                                <Typography variant="body2">
                                    {displayMoney(preview?.pet_deposit)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">
                                    One-time pet fee
                                </Typography>
                                <Typography variant="body2">
                                    {displayMoney(preview?.pet_fee)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">
                                    Security deposit
                                </Typography>
                                <Typography variant="body2">
                                    {displayMoney(preview?.security_deposit)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={
                            createNewLeaseOffer.isLoading ||
                            unitConfigId === '' ||
                            !hasBaseRent ||
                            monthlyTotal <= 0 ||
                            !startDate ||
                            !endDate ||
                            record?.leadId == null
                        }
                        onClick={handleSend}
                    >
                        {createNewLeaseOffer.isLoading
                            ? 'Sending…'
                            : 'Send offer'}
                    </Button>
                </DialogActions>
            </Dialog>
            <PremiumModal
                open={premiumModalOpen}
                defaultActive
                handleClose={() => setPremiumModalOpen(false)}
                onCreated={(premium) => {
                    setCreatedPremiums((current) => [...current, premium])
                    addId(premium.id, setPremiumIds)
                }}
            />
            <ConcessionModal
                open={concessionModalOpen}
                defaultActive
                handleClose={() => setConcessionModalOpen(false)}
                onCreated={(concession) => {
                    setCreatedConcessions((current) => [...current, concession])
                    addId(concession.id, setConcessionIds)
                }}
            />
        </>
    )
}
